'use client'

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useCart } from '@domain/marketplace/api/useCart'
import { useAddCartItem } from '@domain/marketplace/api/useAddCartItem'
import { useRemoveCartItem } from '@domain/marketplace/api/useRemoveCartItem'
import type { AddCartItemInput, CartItemDto } from '@domain/marketplace/api/cart.types'
import { useIsAuthenticated } from '@domain/auth/Auth.context'
import { useCurrentBreakpoint } from '@hooks/useCurrentBreakpoint.hook'
import { useApi } from '@api/useApi.hook'
import { appConfig } from '@config'
import { QueryKey } from '@api'
import { queryClient } from '@providers/react-query'

const GUEST_CART_STORAGE_KEY = 'MARKETPLACE_GUEST_CART'

type GuestCartItem = {
  id: string
  contractAddress: string
  nftId: number
  quantity: number
}

type MarketplaceCartContextType = {
  items: CartItemDto[]
  isLoading: boolean
  count: number
  autoSwapEnabled: boolean
  setAutoSwapEnabled: (value: boolean | ((prev: boolean) => boolean)) => void
  isOpen: boolean
  isPurchasing: boolean
  setIsPurchasing: (v: boolean) => void
  openCart: () => void
  closeCart: () => void
  addItem: (
    input: {
      contractAddress: string
      nftId: number
      quantity?: number
    }[],
  ) => Promise<void>
  removeItem: (itemId: string) => Promise<void>
  isInCart: (contractAddress: string, nftId: number) => boolean
}

const MarketplaceCartContext = createContext<MarketplaceCartContextType | undefined>(
  undefined,
)

export const MarketplaceCartProvider = ({ children }: PropsWithChildren) => {
  const [guestItems, setGuestItems] = useState<GuestCartItem[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [autoSwapEnabled, setAutoSwapEnabled] = useState(true)
  const cartQuery = useCart()
  const { post } = useApi()
  const addCartItem = useAddCartItem()
  const removeCartItem = useRemoveCartItem()
  const isAuthenticated = useIsAuthenticated()
  const { isMobile } = useCurrentBreakpoint()
  const isSyncingRef = useRef(false)

  // Stable refs so useMemo below doesn't depend on the full mutation objects
  const addCartItemRef = useRef(addCartItem)
  addCartItemRef.current = addCartItem
  const removeCartItemRef = useRef(removeCartItem)
  removeCartItemRef.current = removeCartItem
  const isMobileRef = useRef(isMobile)
  isMobileRef.current = isMobile

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(GUEST_CART_STORAGE_KEY)
      if (!raw) {
        return
      }
      const parsed = JSON.parse(raw) as GuestCartItem[]
      if (Array.isArray(parsed)) {
        setGuestItems(parsed)
      }
    } catch {
      setGuestItems([])
    }
  }, [])

  useEffect(() => {
    try {
      window.localStorage.setItem(GUEST_CART_STORAGE_KEY, JSON.stringify(guestItems))
    } catch {
      // Ignore quota/storage errors to avoid blocking cart interactions.
    }
  }, [guestItems])

  useEffect(() => {
    if (!isAuthenticated || guestItems.length === 0 || isSyncingRef.current) return

    isSyncingRef.current = true

    const syncGuestCartToServer = async () => {
      try {
        // Call the API directly (not via mutation) to avoid N onSuccess
        // invalidations — one single invalidation after the full loop instead
        await post(appConfig.endpoints.v1.cart.items, { disableAutoSnakeCase: true })({
          items: guestItems.map((item) => ({
            contractAddress: item.contractAddress,
            nftId: Number(item.nftId),
          })),
        })

        setGuestItems([])
        window.localStorage.removeItem(GUEST_CART_STORAGE_KEY)
        await queryClient.invalidateQueries({ queryKey: [QueryKey.Cart] })
      } finally {
        isSyncingRef.current = false
      }
    }

    void syncGuestCartToServer()
  }, [isAuthenticated, guestItems, post])

  const serverItems = cartQuery.data?.items || []
  const items = isAuthenticated ? serverItems : guestItems
  const count = items.reduce((sum, item) => sum + (item.quantity ?? 1), 0)

  const isInCart = useCallback(
    (contractAddress: string, nftId: number) =>
      items.some(
        (item) =>
          item.contractAddress.toLowerCase() === contractAddress.toLowerCase() &&
          Number(item.nftId) === Number(nftId),
      ),
    [items],
  )

  const addItem = useCallback(
    async (inputs: AddCartItemInput[]) => {
      if (!inputs.length) return

      if (isAuthenticated) {
        await addCartItemRef.current.mutateAsync({ items: inputs })
        if (!isMobileRef.current) setIsOpen(true)
        return
      }

      setGuestItems((current) => {
        const existingKeys = new Set(
          current.map(
            (item) => `${item.contractAddress.toLowerCase()}:${Number(item.nftId)}`,
          ),
        )
        const additions: GuestCartItem[] = []

        for (const input of inputs) {
          const normalizedAddress = input.contractAddress.toLowerCase()
          const itemKey = `${normalizedAddress}:${Number(input.nftId)}`
          if (existingKeys.has(itemKey)) continue

          existingKeys.add(itemKey)
          additions.push({
            id: itemKey,
            contractAddress: input.contractAddress,
            nftId: Number(input.nftId),
            quantity: input.quantity ?? 1,
          })
        }

        if (!additions.length) return current
        return [...current, ...additions]
      })
      if (!isMobileRef.current) setIsOpen(true)
    },
    [isAuthenticated],
  )

  const removeItem = useCallback(
    async (itemId: string) => {
      if (isAuthenticated) {
        await removeCartItemRef.current.mutateAsync(itemId)
        return
      }
      setGuestItems((current) => current.filter((item) => item.id !== itemId))
    },
    [isAuthenticated],
  )

  const value = useMemo<MarketplaceCartContextType>(
    () => ({
      items,
      isOpen,
      autoSwapEnabled,
      setAutoSwapEnabled,
      isPurchasing,
      setIsPurchasing,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      isLoading: isAuthenticated
        ? cartQuery.isLoading || addCartItem.isLoading || removeCartItem.isLoading
        : false,
      count,
      addItem,
      removeItem,
      isInCart,
    }),
    [
      items,
      isAuthenticated,
      cartQuery.isLoading,
      addCartItem.isLoading,
      removeCartItem.isLoading,
      count,
      isOpen,
      autoSwapEnabled,
      isPurchasing,
      addItem,
      removeItem,
      isInCart,
    ],
  )

  return (
    <MarketplaceCartContext.Provider value={value}>
      {children}
    </MarketplaceCartContext.Provider>
  )
}

export const useMarketplaceCart = () => {
  const context = useContext(MarketplaceCartContext)
  if (!context) {
    throw new Error('useMarketplaceCart must be used within MarketplaceCartProvider')
  }
  return context
}
